import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import styled from 'react-emotion'
import { Manager, Popper, Reference } from 'react-popper'
import { compose, lifecycle } from 'recompose'
import { border, borderRadius, color, menuWidth, space, z } from '../../style'
import { StyledFormElement as FormElement } from './FormElement'
export { default as Tooltip2 } from '../../../common/components/Tooltip'

const distance = 10

export const TooltipComponent = styled(FormElement)(
  ({ placement, white = false, multiline }) => {
    const style = {
      fontSize: 12,
      fontWeight: 500,
      maxWidth: multiline && menuWidth,
      background: white ? '#fff' : color.black,
      borderRadius,
      color: white ? color.black : '#fff',
      border: white ? border : `1px solid ${color.black}`,
      zIndex: z.tooltip,
      a: {
        textDecoration: 'none',
        color: 'inherit'
      }
    }
    if (placement === 'top') {
      style.marginBottom = distance
      style['&:before'] = {
        // Filler to bridge gap between tooltip and parent
        content: '""',
        position: 'absolute',
        bottom: -distance - 1,
        height: distance + 2,
        width: '100%',
        left: 0
      }
    }
    if (placement === 'bottom') {
      style.marginTop = distance
      style['&:before'] = {
        // Filler to bridge gap between tooltip and parent
        content: '""',
        position: 'absolute',
        top: -distance - 1,
        height: distance + 2,
        width: '100%',
        ledt: 0
      }
    }
    if (placement === 'right') {
      style.marginLeft = distance
      style['&:before'] = {
        // Filler to bridge gap between tooltip and parent
        content: '""',
        position: 'absolute',
        left: -distance - 1,
        width: distance + 2,
        height: '100%',
        top: 0
      }
    }
    if (placement === 'left') {
      style.marginRight = distance
      style['&:before'] = {
        // Filler to bridge gap between tooltip and parent
        content: '""',
        position: 'absolute',
        right: -distance - 1,
        width: distance + 2,
        height: '100%',
        top: 0
      }
    }

    return style
  }
)

const Tooltip = compose(
  lifecycle({
    componentWillReceiveProps(newProps) {
      // when popper first figures out placement, tell it to think harder
      // tooltips on the left will be incorrectly placed otherwise
      // https://github.com/FezVrasta/react-popper/issues/88
      if (!this.props.placement && newProps.placement) {
        this.props.scheduleUpdate()
      }
    }
  })
)(({ children, placement, white, arrowProps, ...props }) => (
  <TooltipComponent
    white={white}
    placement={placement}
    className="ignore-react-onclickoutside"
    {...props}
  >
    {children}
  </TooltipComponent>
))

export default Tooltip

export class WithTooltip extends Component {
  state = { showing: false }
  timer = null
  show = (delay = 0) => {
    this.timer && clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.setState({ showing: true })
    }, delay)
  }
  hide = (delay = 0) => {
    this.timer && clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.setState({ showing: false })
    }, delay)
  }
  componentWillUnmount() {
    this.timer && clearTimeout(this.timer)
  }
  render() {
    const {
      children,
      placement = 'right',
      modifiers = {
        preventOverflow: {
          boundariesElement: 'viewport',
          padding: space.large
        }
      },
      tooltip
    } = this.props
    return (
      <Manager>
        <Reference>
          {({ ref }) => children({ ref, show: this.show, hide: this.hide })}
        </Reference>
        {this.state.showing &&
          ReactDOM.createPortal(
            <Popper positionFixed placement={placement} modifiers={modifiers}>
              {props => tooltip({ ...props, show: this.show, hide: this.hide })}
            </Popper>,
            document.body
          )}
      </Manager>
    )
  }
}

const cache = []
const mergeRefs = (...refs) => {
  const cached = cache.find(item =>
    item.refs.every((ref, i) => ref === refs[i])
  )
  if (cached) return cached.func

  const func = r => {
    refs.forEach(ref => ref && ref(r))
  }
  cache.push({ refs, func })
  return func
}

export const withHoverTooltip = Component =>
  React.forwardRef(({ tooltip, ...props }, outerRef) => (
    <WithTooltip
      placement={tooltip.placement}
      tooltip={({ ref, show, hide, ...popperProps }) => (
        <Tooltip
          onMouseEnter={() => show(0)}
          onMouseLeave={() => hide(0)}
          innerRef={ref}
          {...popperProps}
        >
          {tooltip.text || tooltip}
        </Tooltip>
      )}
    >
      {({ ref, show, hide }) => (
        <Component
          {...props}
          innerRef={mergeRefs(ref, outerRef)}
          onMouseEnter={() => show(0)}
          onMouseLeave={() => hide(0)}
        />
      )}
    </WithTooltip>
  ))
